import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, tap } from 'rxjs';
import { environment } from 'shared/environments/environment';
import { AnalyticsService } from 'shared/src/services/analytics.service';
import { UserInfoService } from 'shared/src/services/user-info.service';
import { CustomizerSettingsService } from '../../../shared/src/services/customizer-settings.service';
import { FooterComponent } from './components/common/footer/footer.component';
import { HeaderComponent } from './components/common/header/header.component';
import { AudioPanelComponent } from './features/player/audio-panel/audio-panel.component';
import { PlayerService } from './features/player/player.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, HeaderComponent, FooterComponent, AudioPanelComponent],
})
export class AppComponent implements OnInit {
  title = 'Peech';

  isBlankPage: boolean = false;
  isLogoShown: boolean = false;
  isAudioPanelShown: boolean = false;
  isIdleNoticeShown: boolean = false;

  private blankPageRoutes = [
    '/error-500',
    '/authentication/login',
    '/authentication/logout',
    '/login',
    '/user-discount',
    '/holiday-discount',
    '/checkout',
    '/credits',
    '/credits/success',
  ];

  private logoShownRoutes = [
    '/user-discount',
    '/holiday-discount',
    '/checkout',
    '/credits',
    '/credits/success',
  ];

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private themeService: CustomizerSettingsService,
    private analyticsService: AnalyticsService,
    private userInfo: UserInfoService,
    private player: PlayerService
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        tap(() => {
          this.isBlankPage = this.blankPageRoutes.some(route => this.router.url.includes(route));
          this.isLogoShown = this.logoShownRoutes.some(route => this.router.url.includes(route));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // The fixed panel needs matching bottom clearance on the page content, so the last
    // paragraph / footer can scroll out from under it.
    this.player.snapshot$
      .pipe(
        tap(snapshot => {
          this.isAudioPanelShown = snapshot.status !== 'idle';
          this.isIdleNoticeShown = snapshot.idlePaused;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.startAnalytics();
    this.setTheme();
    this.handleQueryParams();
  }

  private async startAnalytics() {
    await this.analyticsService.init(environment.appVersion, true);
  }

  private handleQueryParams() {
    this.userInfo.hydrateFromUrl();
  }

  // OS-driven, exactly like the funnels (funnel/src/app/app.component.ts): initial value
  // from prefers-color-scheme, then live updates on OS changes. No in-app toggle.
  private setTheme() {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.themeService.setTheme(prefersDark);

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
      this.themeService.setTheme(e.matches);
    });
  }
}
